import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Add as AddIcon,
  CheckCircleOutlined,
  ChatBubbleOutline as CommentIcon,
  ErrorOutlineOutlined,
  GroupAddOutlined as GroupAddIcon,
  HighlightOffOutlined,
  RadioButtonUncheckedOutlined,
  DeleteOutline as DeleteIcon,
  DownloadOutlined as DownloadIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import { SESSION_PERMISSIONS, useHasPermission } from 'Utils/permissions';
import { ATTENDANCE_STATUS_META } from '../utils/session-labels';
import { formatSessionDateTime } from '../utils/session-datetime';

const STATUS_ICONS = {
  present: <CheckCircleOutlined fontSize="small" />,
  absent: <HighlightOffOutlined fontSize="small" />,
  justified: <ErrorOutlineOutlined fontSize="small" />,
  pending: <RadioButtonUncheckedOutlined fontSize="small" />,
};

const STATUS_OPTIONS = ['present', 'absent', 'justified'];

const FILTER_OPTIONS = [
  { value: 'all', meta: { color: colors.GRAY_DARKER, bg: colors.BLUE_GRAY } },
  { value: 'present', meta: ATTENDANCE_STATUS_META.present },
  { value: 'absent', meta: ATTENDANCE_STATUS_META.absent },
  { value: 'justified', meta: ATTENDANCE_STATUS_META.justified },
  { value: 'pending', meta: ATTENDANCE_STATUS_META.pending },
];

const styles = {
  card: { borderRadius: 2, overflow: 'hidden', border: `1px solid ${colors.GRAY_HR}`, boxShadow: 'none' },
  header: {
    p: 2.25,
    borderBottom: `1px solid ${colors.GRAY_HR}`,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap',
  },
  // Cap the row region so the whole dialog body stays compact: the filter
  // toolbar and pagination footer are always visible while rows scroll inside.
  // The offset accounts for AppBar + header card + filter + pagination.
  rowsScroll: {
    maxHeight: { xs: 'calc(100vh - 500px)', md: 'calc(100vh - 450px)' },
    overflowY: 'auto',
  },
  filterBtn: (active, color) => ({
    px: 1.5,
    py: 0.5,
    borderRadius: 999,
    border: `1px solid ${active ? color : colors.GRAY_HR}`,
    bgcolor: active ? `${color}1A` : '#fff',
    color: active ? color : colors.GRAY_DARKER,
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'none',
    minHeight: 28,
  }),
  row: {
    px: 2.25,
    py: 1.5,
    // Locked min-height prevents the row from growing the first time it's
    // marked: the "lastUpdatedBy" sub-line only appears after the first save,
    // and without a reserved slot every row below would shift down.
    minHeight: 68,
    borderBottom: `1px solid ${colors.GRAY_HR}`,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    '&:last-child': { borderBottom: 'none' },
  },
  avatar: bg => ({
    width: 36,
    height: 36,
    borderRadius: '50%',
    bgcolor: bg || colors.GREEN,
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    fontSize: 12.5,
    fontWeight: 600,
    flexShrink: 0,
  }),
  statusBtn: (active, meta) => ({
    width: 34,
    height: 34,
    minWidth: 0,
    borderRadius: 1.25,
    border: `1px solid ${active ? meta.color : colors.GRAY_HR}`,
    bgcolor: active ? meta.bg : '#fff',
    color: active ? meta.color : colors.GRAY_DARK,
    '&:hover': { bgcolor: active ? meta.bg : colors.BLUE_GRAY },
  }),
  empty: { textAlign: 'center', py: 6, color: colors.GRAY_DARK, px: 2 },
};

const initials = fullName => {
  if (!fullName) return '?';
  return (
    fullName
      .split(' ')
      .slice(0, 2)
      .map(w => w[0]?.toUpperCase())
      .join('') || '?'
  );
};

const labelKey = code => `attendanceStatus.${code === null ? 'pending' : code}`;

/**
 * Pure presentational + interaction. Receives attendance rows from the parent
 * (which owns `useAttendanceApi`) and emits events upward — never mutates.
 */
export default function AttendanceTable({
  attendances,
  loading,
  filter,
  onFilterChange,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onUpdateStatus,
  onUpdateComment,
  onRemove,
  onAddAttendee,
  onAddAllFromCourse,
  addAllSubmitting,
  isImmutable,
  onExport,
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  search,
  onSearchChange,
}) {
  const { t2 } = useTranslation('EditCourseSessions');
  const canUpdateRaw = useHasPermission(SESSION_PERMISSIONS.ATTENDANCE_UPDATE);
  const canRead = useHasPermission(SESSION_PERMISSIONS.READ);
  // When the session is manually completed, the backend rejects every
  // attendance mutation with `session_completed_immutable`. We mirror that
  // server rule on the client by collapsing `canUpdate` to read-only.
  const canUpdate = canUpdateRaw && !isImmutable;

  const [commentDrafts, setCommentDrafts] = useState({});
  // Rows where the user explicitly clicked the comment icon. Always starts
  // empty; the icon-with-badge is the resting state for ALL rows (with or
  // without a saved comment) — click to expand, blur to collapse.
  const [expandedComments, setExpandedComments] = useState(() => new Set());

  const expandComment = userId => {
    setExpandedComments(prev => {
      if (prev.has(userId)) return prev;
      const next = new Set(prev);
      next.add(userId);
      return next;
    });
  };

  const collapseComment = userId => {
    setExpandedComments(prev => {
      if (!prev.has(userId)) return prev;
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
  };

  const visibleSelectedCount = attendances.filter(a => selectedIds?.has(a.userId)).length;
  const allSelected = attendances.length > 0 && visibleSelectedCount === attendances.length;

  const handleCommentBlur = (attendance, draft) => {
    collapseComment(attendance.userId);
    if (draft === (attendance.comment || '')) return;
    onUpdateComment(attendance, draft);
  };

  return (
    <Card sx={styles.card} elevation={0}>
      <Box sx={styles.header}>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{t2('detail.attendanceSection')}</Typography>
        <Chip
          label={totalItems}
          size="small"
          sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: colors.BLUE_GRAY, color: colors.GRAY_DARKER }}
        />
        <TextField
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          size="small"
          placeholder={t2('attendance.searchPlaceholder')}
          color="primary"
          sx={{
            flex: '1 1 220px',
            maxWidth: 320,
            '& .MuiOutlinedInput-root': { borderRadius: 999, fontSize: 13, height: 34, bgcolor: '#fff' },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: colors.GRAY_DARK }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')} aria-label="clear search">
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Stack direction="row" spacing={0.5}>
          {FILTER_OPTIONS.map(opt => (
            <Button
              key={opt.value}
              size="small"
              onClick={() => onFilterChange(opt.value)}
              sx={styles.filterBtn(filter === opt.value, opt.meta.color)}
              disableElevation
            >
              {opt.value === 'all' ? t2('toolbar.filterAll') : t2(labelKey(opt.value))}
            </Button>
          ))}
        </Stack>
      </Box>

      {(canUpdate || (canRead && attendances.length > 0)) && (
        <Box
          sx={{
            px: 2.25,
            py: 1,
            borderBottom: `1px solid ${colors.GRAY_HR}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {canUpdate && attendances.length > 0 && (
            <>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={visibleSelectedCount > 0 && !allSelected}
                onChange={onToggleSelectAll}
              />
              <Typography sx={{ fontSize: 12.5, color: colors.GRAY_DARK }}>
                {visibleSelectedCount > 0 ? t2('attendance.bulkBar', [visibleSelectedCount]) : t2('toolbar.filterAll')}
              </Typography>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          {canRead && attendances.length > 0 && (
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={onExport}
              sx={{ textTransform: 'none', color: colors.GRAY_DARKER }}
            >
              {t2('toolbar.exportAttendance')}
            </Button>
          )}
          {canUpdate && (
            <Button
              size="small"
              startIcon={<GroupAddIcon />}
              onClick={onAddAllFromCourse}
              disabled={addAllSubmitting}
              sx={{ textTransform: 'none', color: colors.GREEN_LEAF, fontWeight: 600 }}
            >
              {t2('attendance.addAllFromCourse')}
            </Button>
          )}
          {canUpdate && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={onAddAttendee}
              sx={{ textTransform: 'none', color: colors.GREEN, fontWeight: 600 }}
            >
              {t2('attendance.addAttendee')}
            </Button>
          )}
        </Box>
      )}

      <Box sx={styles.rowsScroll}>
        {!loading && attendances.length === 0 && (
          <Box sx={styles.empty}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {search ? t2('empty.noAttendeesForSearch') : t2('empty.noAttendees')}
            </Typography>
            {!search && canUpdate && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAddAttendee}
                sx={{ textTransform: 'none', color: colors.GREEN, borderColor: colors.GREEN }}
              >
                {t2('empty.noAttendeesCta')}
              </Button>
            )}
          </Box>
        )}

        {attendances.map(att => {
          const userMeta = att.user || { fullName: '—' };
          const draft = commentDrafts[att.userId] ?? (att.comment || '');
          const hasComment = !!(att.comment && att.comment.length > 0);
          const isCommentVisible = expandedComments.has(att.userId);
          return (
            <Box key={att.id || att.userId} sx={styles.row}>
              {canUpdate && (
                <Checkbox
                  size="small"
                  checked={selectedIds?.has(att.userId) || false}
                  onChange={() => onToggleSelect(att.userId)}
                />
              )}
              <Box sx={styles.avatar()}>{initials(userMeta.fullName)}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: colors.BLACK }}>
                  {userMeta.fullName}
                </Typography>
                {userMeta.payrollNum && (
                  <Typography sx={{ fontSize: 11, color: colors.GRAY_DARK, mt: 0.25 }}>
                    {t2('attendance.payrollNum')}: {userMeta.payrollNum}
                  </Typography>
                )}
                {att.lastUpdatedAt && (
                  <Typography sx={{ fontSize: 11, color: colors.GRAY_DARK, mt: 0.25 }}>
                    {t2('detail.lastUpdatedBy', [userMeta.fullName, formatSessionDateTime(att.lastUpdatedAt)])}
                  </Typography>
                )}
              </Box>

              {canUpdate ? (
                isCommentVisible ? (
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder={t2('attendance.commentPlaceholder')}
                    value={draft}
                    autoFocus
                    onChange={e => setCommentDrafts(prev => ({ ...prev, [att.userId]: e.target.value }))}
                    onBlur={() => handleCommentBlur(att, draft)}
                    inputProps={{ maxLength: 500 }}
                    sx={{ width: 220, '& .MuiOutlinedInput-root': { fontSize: 12.5, height: 32 } }}
                  />
                ) : (
                  <Tooltip
                    title={hasComment ? t2('attendance.viewCommentTooltip') : t2('attendance.addCommentTooltip')}
                  >
                    <IconButton size="small" onClick={() => expandComment(att.userId)}>
                      <Badge
                        variant="dot"
                        color="success"
                        invisible={!hasComment}
                        sx={{ '& .MuiBadge-badge': { right: 2, top: 2 } }}
                      >
                        <CommentIcon
                          sx={{
                            fontSize: 18,
                            color: hasComment ? colors.GREEN : colors.GRAY_DARK,
                          }}
                        />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )
              ) : att.comment ? (
                <Tooltip title={att.comment}>
                  <IconButton size="small" disabled>
                    <Badge variant="dot" color="success" sx={{ '& .MuiBadge-badge': { right: 2, top: 2 } }}>
                      <CommentIcon sx={{ fontSize: 18, color: colors.GREEN }} />
                    </Badge>
                  </IconButton>
                </Tooltip>
              ) : null}

              <Stack direction="row" spacing={0.5}>
                {STATUS_OPTIONS.map(s => {
                  const meta = ATTENDANCE_STATUS_META[s];
                  const active = att.status === s;
                  return (
                    <Tooltip key={s} title={t2(labelKey(s))}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!canUpdate}
                          onClick={() => onUpdateStatus(att, s)}
                          sx={styles.statusBtn(active, meta)}
                        >
                          {STATUS_ICONS[s]}
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                })}
              </Stack>

              {canUpdate && (
                <Tooltip title={t2('attendance.remove')}>
                  <IconButton size="small" onClick={() => onRemove(att)}>
                    <DeleteIcon sx={{ fontSize: 18, color: colors.RED }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>

      {totalItems > 0 && (
        <TablePagination
          component="div"
          count={totalItems}
          page={Math.max(0, page - 1)}
          onPageChange={(_event, nextZeroBased) => onPageChange(nextZeroBased + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={event => onPageSizeChange(parseInt(event.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t2('attendance.rowsPerPage')}
          sx={{
            borderTop: `1px solid ${colors.GRAY_HR}`,
            '& .MuiTablePagination-toolbar': { minHeight: 44 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: 12.5,
              color: colors.GRAY_DARKER,
            },
          }}
        />
      )}
    </Card>
  );
}

AttendanceTable.propTypes = {
  attendances: PropTypes.array,
  loading: PropTypes.bool,
  filter: PropTypes.string,
  onFilterChange: PropTypes.func.isRequired,
  selectedIds: PropTypes.instanceOf(Set),
  onToggleSelect: PropTypes.func.isRequired,
  onToggleSelectAll: PropTypes.func.isRequired,
  onUpdateStatus: PropTypes.func.isRequired,
  onUpdateComment: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAddAttendee: PropTypes.func.isRequired,
  onAddAllFromCourse: PropTypes.func.isRequired,
  addAllSubmitting: PropTypes.bool,
  isImmutable: PropTypes.bool,
  onExport: PropTypes.func.isRequired,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  search: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
};

AttendanceTable.defaultProps = {
  attendances: [],
  loading: false,
  filter: 'all',
  selectedIds: new Set(),
  addAllSubmitting: false,
  isImmutable: false,
  page: 1,
  pageSize: 25,
  totalItems: 0,
  search: '',
};
